/**
 * Componente: QueueStepsTimeline
 * Exibe timeline visual dos steps de execução de um flow
 * Mostra status, tempo e tipo de cada nó processado
 */

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Play, MessageSquare, MousePointer, List, GitBranch,
  Clock, Zap, Globe, Flag, TextCursor, HelpCircle,
  CheckCircle, XCircle, Loader2, Circle
} from 'lucide-react';

// Interface para step de execução
// Interface para output_data de steps de pergunta
interface StepOutputData {
  user_response?: string;
  mapped_response?: string;
  variable_name?: string;
  node_type?: string;
  selected_option_index?: number | null;
  [key: string]: unknown;
}

export interface ExecutionStep {
  id: string;
  node_id: string;
  node_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
  input_data: Record<string, unknown> | null;
  output_data: StepOutputData | null;
}

interface QueueStepsTimelineProps {
  steps: ExecutionStep[];
  className?: string;
}

// Configuração visual por tipo de nó
const nodeTypeConfig: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  start: { icon: <Play className="h-3.5 w-3.5" />, color: 'text-green-500', label: 'Início' },
  message_text: { icon: <MessageSquare className="h-3.5 w-3.5" />, color: 'text-blue-500', label: 'Mensagem' },
  message_media: { icon: <MessageSquare className="h-3.5 w-3.5" />, color: 'text-blue-500', label: 'Mídia' },
  question_buttons: { icon: <MousePointer className="h-3.5 w-3.5" />, color: 'text-purple-500', label: 'Botões' },
  question_list: { icon: <List className="h-3.5 w-3.5" />, color: 'text-purple-500', label: 'Lista' },
  condition: { icon: <GitBranch className="h-3.5 w-3.5" />, color: 'text-orange-500', label: 'Condição' },
  delay: { icon: <Clock className="h-3.5 w-3.5" />, color: 'text-muted-foreground', label: 'Delay' },
  action: { icon: <Zap className="h-3.5 w-3.5" />, color: 'text-yellow-500', label: 'Ação' },
  add_tag: { icon: <Zap className="h-3.5 w-3.5" />, color: 'text-yellow-500', label: 'Tag' },
  transfer_human: { icon: <Zap className="h-3.5 w-3.5" />, color: 'text-yellow-500', label: 'Transferir' },
  transfer_department: { icon: <Zap className="h-3.5 w-3.5" />, color: 'text-yellow-500', label: 'Departamento' },
  update_contact: { icon: <Zap className="h-3.5 w-3.5" />, color: 'text-yellow-500', label: 'Atualizar' },
  webhook: { icon: <Globe className="h-3.5 w-3.5" />, color: 'text-cyan-500', label: 'Webhook' },
  set_variable: { icon: <TextCursor className="h-3.5 w-3.5" />, color: 'text-indigo-500', label: 'Variável' },
  input_data: { icon: <TextCursor className="h-3.5 w-3.5" />, color: 'text-indigo-500', label: 'Input' },
  end: { icon: <Flag className="h-3.5 w-3.5" />, color: 'text-green-600', label: 'Fim' },
  goto: { icon: <GitBranch className="h-3.5 w-3.5" />, color: 'text-orange-500', label: 'Ir Para' },
  ab_test: { icon: <GitBranch className="h-3.5 w-3.5" />, color: 'text-pink-500', label: 'Teste A/B' },
};

// Configuração visual por status
const statusConfig: Record<string, { icon: React.ReactNode; color: string; bgColor: string }> = {
  completed: { icon: <CheckCircle className="h-4 w-4" />, color: 'text-green-500', bgColor: 'bg-green-500/10' },
  running: { icon: <Loader2 className="h-4 w-4 animate-spin" />, color: 'text-yellow-500', bgColor: 'bg-yellow-500/10' },
  failed: { icon: <XCircle className="h-4 w-4" />, color: 'text-red-500', bgColor: 'bg-red-500/10' },
  pending: { icon: <Circle className="h-4 w-4" />, color: 'text-muted-foreground', bgColor: 'bg-muted' },
};

/**
 * Calcula duração do step em formato legível
 */
function formatDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return '...';
  
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const durationMs = end - start;
  
  if (durationMs < 1000) {
    return `${durationMs}ms`;
  } else if (durationMs < 60000) {
    return `${(durationMs / 1000).toFixed(1)}s`;
  } else {
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  }
}

export function QueueStepsTimeline({ steps, className }: QueueStepsTimelineProps) {
  if (!steps || steps.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-2">
        Nenhum step de execução encontrado
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Resumo visual inline */}
      <div className="flex items-center gap-1 text-xs text-muted-foreground flex-wrap">
        {steps.map((step, index) => {
          const config = nodeTypeConfig[step.node_type] || { 
            icon: <HelpCircle className="h-3 w-3" />, 
            color: 'text-muted-foreground', 
            label: step.node_type 
          };
          const status = statusConfig[step.status] || statusConfig.pending;
          
          return (
            <span key={step.id} className="flex items-center gap-1">
              <span className={cn('flex items-center gap-0.5', status.color)}>
                {config.icon}
              </span>
              {index < steps.length - 1 && <span className="text-muted-foreground/50">→</span>}
            </span>
          );
        })}
      </div>

      {/* Lista detalhada de steps */}
      <div className="space-y-1.5">
        {steps.map((step, index) => {
          const config = nodeTypeConfig[step.node_type] || { 
            icon: <HelpCircle className="h-3.5 w-3.5" />, 
            color: 'text-muted-foreground', 
            label: step.node_type 
          };
          const status = statusConfig[step.status] || statusConfig.pending;
          const duration = formatDuration(step.started_at, step.completed_at);

          return (
            <div 
              key={step.id} 
              className={cn(
                'flex items-start gap-2 p-2 rounded-md text-sm',
                status.bgColor
              )}
            >
              {/* Número do step */}
              <span className="text-muted-foreground font-mono text-xs w-4 shrink-0">
                {index + 1}.
              </span>

              {/* Ícone de status */}
              <span className={cn('shrink-0 mt-0.5', status.color)}>
                {status.icon}
              </span>

              {/* Tipo do nó */}
              <span className={cn('shrink-0', config.color)}>
                {config.icon}
              </span>

              {/* Info do step */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    {config.label}
                  </Badge>
                  <span className="text-xs text-muted-foreground font-mono truncate">
                    {step.node_id}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({duration})
                  </span>
                </div>

                {/* Resposta do usuário (para steps de pergunta) */}
                {step.output_data?.user_response && (
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <MessageSquare className="h-3 w-3 text-blue-500 shrink-0" />
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                      "{step.output_data.mapped_response || step.output_data.user_response}"
                    </span>
                    {step.output_data.variable_name && (
                      <span className="text-[10px] text-muted-foreground">
                        → {step.output_data.variable_name}
                      </span>
                    )}
                  </div>
                )}

                {/* Mensagem de erro */}
                {step.error_message && (
                  <p className="text-xs text-red-500 mt-1 break-words">
                    {step.error_message}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
